import Form from '../../shared/global/form';
import { findClosest } from '../FindClosest';
import { setupAutocomplete } from '../../shared/global/autocomplete';
import { ChangePopupStore } from './ChangePopupStore';
import { FormDataToJSON } from '../../shared/utils/convert';
import { handleLocalSet } from '.';
import { getOneTrustConsentStatus } from '../../shared/utils/oneTrust/getStatusData';
import { GroupIdType } from '../../types/oneTrust';
import { ConsentManager } from '../../shared/utils/oneTrust/consentManager';
import { subscribeToConsentChanged } from '../../shared/utils/oneTrust/subscribeToConsentChanged';
import { oneTrustIsInitialized } from '../../shared/utils/oneTrust/isInitialized';

export default class FormDeliveryAddress extends Form {
    maxDistance: number;
    readonly store: ChangePopupStore;

    constructor(wrapper: HTMLFormElement, options: FormOptions, store: ChangePopupStore) {
        super(wrapper, options);

        this.store = store;
    }

    initValidationEvents() {
        this.parseConstraints();

        const inputElements = this.wrapper.querySelectorAll('input[type=text]');

        inputElements.forEach((element: Element) => {
            element.addEventListener('input', e => {
                const _target = <HTMLInputElement>e.target;
                if (_target) {
                    this.errorFlags.touched = true;
                    if (this.errorFlags.submit /*||  this.errorFlags[e.target.name]*/) {
                        const _errors = this.validate();
                        this.showErrorsForElement(
                            _target,
                            _errors && _errors[this.parseDOMElementName(_target.name)]
                        );
                    }
                }
            });
            element.addEventListener('blur', e => {
                const _target = <HTMLInputElement>e.target;
                if (_target) {
                    _target.value = _target.value.trim();
                    // Show error only when touched
                    if (this.errorFlags.submit || this.errorFlags.touched) {
                        const _errors = this.validate();
                        this.showErrorsForElement(
                            _target,
                            _errors && _errors[this.parseDOMElementName(_target.name)]
                        );
                    }
                }
            });
        });

        this.wrapper.addEventListener('submit', e => {
            e.preventDefault();
            this.errorFlags.submit = true;
            this.submit(e);
        });
    }

    additionalMethods() {
        if (this.wrapper.City) {
            setupAutocomplete(
                this.wrapper.City,
                (prefixValue: string) => {
                    return {
                        api: 'citysearch',
                        data: {
                            prefix: prefixValue
                        }
                    };
                },
                (value: string) => value && value.length > 2,
                this.wrapper.Street
            );
        }

        if (this.wrapper.Street) {
            setupAutocomplete(
                this.wrapper.Street,
                (prefixValue: string) => {
                    return {
                        api: 'streetsearch',
                        data: {
                            city: this.wrapper.City.value,
                            prefix: prefixValue
                        }
                    };
                },
                (value: string) => {
                    return (
                        value &&
                        value.length > 2 &&
                        this.wrapper.City.value &&
                        this.wrapper.City.value.length > 2
                    );
                },
                this.wrapper.HouseNr
            );
        }
    }

    initValidation() {
        this.maxDistance = parseInt(this.wrapper.getAttribute('data-maxdistance')) || 0;
    }

    setupSend() {
        this.showLoader();
        this.clearServerMessage();

        const formData = new FormData(this.wrapper);
        formData.append('DeliveryType', this.store.deliveryType.toString());
        const data = FormDataToJSON(formData, this.dynamicNames);
        this.send(JSON.stringify(data));
    }

    successCallback(response: LocalData) {
        const addressParts = [
            this.wrapper.City.value,
            this.wrapper.Street.value,
            this.wrapper.HouseNr.value,
            this.wrapper.FlatNr.value || ''
        ];

        if (response.Status && response.Data?.Location) {
            // Save typed address in store
            this.store.saveAddress({
                City: addressParts[0],
                Street: addressParts[1],
                HouseNr: addressParts[2],
                FlatNr: addressParts[3],
                LocalId: response.Data.Id
            });
            // Local was found
            const deliveryType = this.store.deliveryType;
            // Check if it's inactive
            if (!response.Data.IsActive && response.Data.InactiveMessage) {
                this.errorCallback(
                    Object.assign(response, {
                        Message: response.Data.InactiveMessage + ' (' + response.Data.Address + ')'
                    })
                );
            }
            // Check delivery
            else if (
                (deliveryType === 1 && !response.Data.IsDeliveryActive) ||
                (deliveryType === 0 && !response.Data.IsPickupActive) ||
                (!response.Data.IsPickupActive && !response.Data.IsDeliveryActive)
            ) {
                const message =
                    !response.Data.IsPickupActive && !response.Data.IsDeliveryActive
                        ? 'all'
                        : deliveryType;

                this.errorCallback(
                    Object.assign(response, {
                        Message: this.store.dictionary.message.inactive[message] + ' (' + response.Data.Address + ')'
                    })
                );
            } else if (deliveryType == 1) {
      
        handleLocalSet({store: this.store, propsLocalId: response.Data.Id});  // <--add_delivery_address
                this.hideLoader();
            }
            else {
                this.showLocalInfo(response.Data, 0);
                this.hideLoader();
            }
        } else if (!getOneTrustConsentStatus(GroupIdType.Functional)) {
            if (oneTrustIsInitialized()) {
                this.errorManage(response.CookieMessage || 'Zaakceptuj funkcjonalne pliki cookie i sprobuj ponownie.');
                const parent = this.serverMessage.parentNode as HTMLElement;
                parent.addEventListener('click', window.OneTrust.ToggleInfoDisplay);
                parent.style.cursor = 'pointer';
                ConsentManager.addCallback(e => {
                    parent.style.cursor = 'unset';
                    parent.removeEventListener('click', window.OneTrust.ToggleInfoDisplay);
                    this.clearServerMessage();
                });
                subscribeToConsentChanged();
            } else {
                // else something is wrong with OneTrust
                this.errorManage(this.getMessage('errors.default'))
            }
        } else {
            // Local was not found so ask Google
            this.runFindClosest(addressParts, response)
        }
    }

    runFindClosest (addressParts: any[], response: LocalData) {
        findClosest(addressParts, this.maxDistance).then(resp => {
            if (resp.Local?.Location) {
                // Save typed address in store
                this.store.saveAddress({
                    City: addressParts[0],
                    Street: addressParts[1],
                    HouseNr: addressParts[2],
                    FlatNr: addressParts[3],
                    LocalId: resp.Local.Id
                });
                // Local with pickup found
                this.showLocalInfo(resp.Local, resp.Distance);
                this.hideLoader();
            } else {
                // Go with message from previous call (local not found)
                this.errorCallback(response);
            }
        });
    }

    showLocalInfo(local: LocalMapData, distance: number) {        
        this.store.changeLocal(local, distance);
    }

    fillInFields(address: SearchLocal) {
        this.wrapper.City.value = address.City;
        this.wrapper.Street.value = address.Street;
        this.wrapper.HouseNr.value = address.HouseNr;
        this.wrapper.FlatNr.value = address.FlatNr;
    }
}
